/**
 * High-priority insights widget for the personal dashboard.
 * Generic - works for any workflow
 */

import { Warning, ArrowRight } from '@phosphor-icons/react'
import { Badge } from '@/components/ui/badge'
import type { DashboardInsight } from '../types'

interface InsightsWidgetProps {
  insights: DashboardInsight[]
  showWorkflowSource?: boolean
  limit?: number
}

export function InsightsWidget({ insights, showWorkflowSource = true, limit = 6 }: InsightsWidgetProps) {
  const highPriorityInsights = insights
    .filter((i) => i.severity === 'critical' || i.severity === 'high')
    .filter((i) => i.status === 'new' || i.status === 'pending_review')
    .slice(0, limit)

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-[#e6e6e6] bg-white">
      <div className="flex items-center gap-2 px-5 pt-5">
        <h3 className="text-xl font-medium leading-[30px] tracking-[0.2px] text-[#1d1d1d]">Critical & High Priority</h3>
        <div className="flex items-center rounded-3xl bg-[#f5f5f5] px-2 py-1">
          <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#272727]">
            {highPriorityInsights.length} issues
          </span>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-auto p-5">
        {highPriorityInsights.length === 0 ? (
          <div className="flex h-full items-center justify-center text-center">
            <div>
              <div className="mb-2 text-4xl">✓</div>
              <div className="font-semibold text-[#12833d]">All Clear!</div>
              <div className="text-sm text-[#535353]">No high-priority issues</div>
            </div>
          </div>
        ) : (
          highPriorityInsights.map((insight) => (
            <div
              key={insight.insightId}
              className="rounded-xl border border-[#e6e6e6] bg-white p-3 transition-all hover:border-[#ff5c04] hover:shadow-sm"
            >
              <div className="mb-2 flex items-start justify-between">
                <div className="flex-1">
                  <div className="mb-1 text-sm font-medium tracking-[0.14px] text-[#1d1d1d]">{insight.title}</div>
                  <div className="text-xs tracking-[0.12px] text-[#727272]">{insight.shortSummary}</div>
                </div>
                <div className={`rounded-3xl px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] ${insight.severity === 'critical' ? 'bg-[#fee7e7] text-[#b41e2e]' : 'bg-[#fff7ed] text-[#c83408]'}`}>
                  {insight.severity}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {showWorkflowSource && (
                    <div className="rounded-3xl bg-[#e9f9ff] px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#0091ff]">
                      {insight.workflowSource}
                    </div>
                  )}
                  <div className="rounded-3xl bg-[#f5f5f5] px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#272727]">
                    {insight.affectedProducts[0]}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#a3a3a3]" />
              </div>
            </div>
          ))
        )}
      </div>

      <div className="border-t border-[#e6e6e6] px-5 py-3 text-xs tracking-[0.12px] text-[#727272]">
        Generic widget - shows insights from any workflow
      </div>
    </div>
  )
}
